import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { campusService, authService } from '../api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Building2, ArrowLeft } from 'lucide-react';
import ListPageHeader from '../components/list/ListPageHeader';
import ListToolbar from '../components/list/ListToolbar';
import ListPagination from '../components/list/ListPagination';
import ListEmptyState from '../components/list/ListEmptyState';
import ListLoadingState from '../components/list/ListLoadingState';
import { ShortCardGrid, ListSection } from '../components/list/ShortCard';
import { CampusShortCard } from '../components/list/ShortCardVariants';

function SwitchCampus() {
    const [campuses, setCampuses] = useState([]);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(12);
    const { user, refreshUser } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        campusService.getCampuses()
            .then((res) => {
                const all = (res.data.results || res.data).filter((c) => c.is_active);
                setCampuses(all);
            })
            .catch(() => toast.error('Failed to load campuses'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        setPage(1);
    }, [search]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return campuses.filter((c) => c.name.toLowerCase().includes(q));
    }, [search, campuses]);

    const paged = useMemo(
        () => filtered.slice((page - 1) * pageSize, page * pageSize),
        [filtered, page, pageSize]
    );

    const handleSwitch = async () => {
        if (!selected) return toast.error('Please select a campus');
        setSubmitting(true);
        try {
            await authService.switchCampus(selected);
            await refreshUser();
            toast.success('Campus switched successfully!');
            navigate('/profile');
        } catch (err) {
            toast.error('Failed to switch campus');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: 'var(--color-bg-primary)', padding: '2rem', color: 'var(--color-text-primary)' }}>
            <div style={{ maxWidth: '900px', margin: '0 auto' }} className="ux-list-page">
                <Link to="/profile" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: 'var(--color-text-secondary)', textDecoration: 'none', marginBottom: '1.5rem', fontSize: '0.95rem' }}>
                    <ArrowLeft size={18} /> Back to Profile
                </Link>

                <ListPageHeader
                    kicker="Organisation"
                    kickerIcon={Building2}
                    title="Switch Campus"
                    description={`Currently: ${user?.campus_name || 'None'}`}
                />

                <div style={{ background: 'var(--color-card-bg)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: '1.25rem', border: '1px solid var(--color-border)' }}>
                    <ListToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search campuses…" />

                    {loading ? (
                        <ListLoadingState label="Loading campuses…" />
                    ) : filtered.length === 0 ? (
                        <ListEmptyState icon={Building2} title="No campuses found" />
                    ) : (
                        <>
                            <ListSection title="Your organisations">
                                <ShortCardGrid>
                                    {paged.map((campus, index) => {
                                        const isCurrent = campus.name === user?.campus_name;
                                        return (
                                            <CampusShortCard
                                                key={campus.id}
                                                campus={campus}
                                                index={index}
                                                selected={selected === campus.id}
                                                isCurrent={isCurrent}
                                                onClick={() => setSelected(campus.id)}
                                            />
                                        );
                                    })}
                                </ShortCardGrid>
                            </ListSection>
                            <ListPagination
                                page={page}
                                pageSize={pageSize}
                                totalCount={filtered.length}
                                onPageChange={setPage}
                                onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                                pageSizes={[8, 12, 24]}
                            />
                        </>
                    )}

                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                        <Link to="/profile" className="btn btn-outline" style={{ flex: 1, justifyContent: 'center' }}>Cancel</Link>
                        <button
                            type="button"
                            onClick={handleSwitch}
                            disabled={!selected || submitting}
                            className="btn btn-primary"
                            style={{ flex: 2, opacity: (!selected || submitting) ? 0.6 : 1 }}
                        >
                            {submitting ? 'Switching…' : 'Switch Campus'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SwitchCampus;
